import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  getFilteredRowModel,
  Row,
} from '@tanstack/react-table';

import { Controller } from 'react-hook-form';

// Mocks
import { operatorsMockData, vehiclesMockData } from './mocks';

// Types
import {
  OperatorsSelectionType,
  TableProps,
  VehiclesSelectionType,
  handleChangeTypes,
  getOperatorsColumnNames,
  getVehiclesColumnNames,
  variantLeftTh,
  variantRightTh,
  ImperativeRefInput,
  NewAction,
} from './types';
import {
  OptionSelected,
  Options,
} from '../../../../inputs/DropDown/DropDownList/types';
import { DriverList } from '../../../../forms/ActionForms/OperatorsAndVehicles/types';

// Styled components
import {
  OperatorsTableContainer,
  VehiclesTableContainer,
  ContainerTable,
  ContainerInput,
  TableRightTh,
  TableLeftTh,
  DropDownInputStyled,
  CheckBoxStyled,
  Img,
  FiltersContainer,
  DropDownFilterStyled,
  InputTextFilterStyled,
} from './styles';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
} from '../../summaryTables/SummaryTablesStyles';
import { Typography } from '../../../Typography';
import { OperatorsAndVehiclesFormValues } from '../../../../forms/ActionForms/OperatorsAndVehicles/types';

// Icons
import RemoveIcon from '../../../../../assets/icons/remove-file-icon.svg';

// Contants
import { formActionNaming } from '../../../../forms/ActionForms/constants';
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { GroupTypes } from '../../../ShowOT/WorkOrder/types';
import SearchBar from '../../../../inputs/SearchBar';
import { OrderState } from '../../../../../types/orders';
import { Role } from '../../../../../types/auth';
import { useAuth } from '../../../../../hooks/useAuth';
import debug from 'debug';

// ⚠️ SINCRONIZACIÓN: Eventos de vehículos
import { dispatchDriverAssigned } from '../../../../../utils/vehicleEvents';
import { removeExcludedVehicle } from '../../../../../utils/excludedVehiclesSync';

/**
 * Volver a marcar un vehículo o a elegir su conductor es decir «este vehículo
 * va»: si el usuario lo había quitado de Vehículos de obra en Maquinaria, deja
 * de estar excluido y el paso de Maquinaria lo vuelve a precargar.
 *
 * 🔴 Se hace AQUÍ, donde ocurre el gesto. Antes lo hacía Maquinaria al oír el
 * aviso `vehicle:driver-assigned`, pero Maquinaria y Operarios son pasos con
 * ruta propia: nunca están montados a la vez y el aviso no lo oía nadie
 * (2026-10-12). Solo en los asistentes de actuación; la misma tabla en el
 * recorrido de vigilancia no tiene Maquinaria.
 */
const liftWorkVehicleExclusion = (vehicleId: string) => {
  const path = window.location.pathname.split('/');
  const isValidation = path.includes('actions-validation');
  if (!isValidation && !path.includes('new-action')) return;
  removeExcludedVehicle(vehicleId, isValidation);
};
import { textSearchFilter } from '../../textSearchFilter';

// Diagnostic log configuration - DISABLED FOR TESTING
// const logSelección = debug('rois:operators:selection');
// const logOperación = debug('rois:operators:selection:operation');
// const logEstado = debug('rois:operators:selection:state');
// const logVehicles = debug('rois:operators:selection:vehicles');

// Create no-op functions to replace debug logs
const logSelección = (...args: any[]) => { };
const logOperación = (...args: any[]) => { };
const logEstado = (...args: any[]) => { };
const logVehicles = (...args: any[]) => { };

// Debug para diagnóstico de rol Tablet y auto-filtro
const logTablet = debug('rois:action:operators-table:tablet');

// Enable logs in development
if (import.meta.env.DEV) {
  debug.enable('rois:action:operators-table:*');
}

export const OperatorsSelectionTable = ({
  data,
  driversCacheKey,
  operatorsData,
  vehiclesData,
  titles,
  control,
  defaultOperators = [],
  defaultVehicles = [],
  defaultDrivers = [],
  tableName = '',
  setDefaultOperators,
  setDefaultVehicles,
  setValue,
  setCacheDrivers,
  isInternal,
  getValues,
  concessions,
  teams = [],
  showCrewStateFilter,
}: TableProps): JSX.Element => {
  const { t } = useTranslation(['common', 'forms', 'tables']);
  const { user } = useAuth();
  const initialData = data?.breadCrumbsData;
  const operatorsDataAll = operatorsData ? operatorsData : operatorsMockData;
  const vehiclesDataAll = vehiclesData ? vehiclesData : vehiclesMockData;

  const [vehiclesDataTable, setVehiclesDataTable] =
    useState<VehiclesSelectionType[]>(vehiclesDataAll);

  const filterDefaultVehicles = Array.isArray(defaultVehicles) ? defaultVehicles.filter((vehicle) => {
    return Object.keys(vehicle).length > 0;
  }) : [];

  // BUG borrador RV (2026-07-12): la caché de conductores era una key GLOBAL
  // ('drivers') compartida entre wizards — sus efectos de conductor-fantasma
  // hacían setValue sobre vehicleTable[<id del wizard anterior>],
  // materializando entradas vacías que el borrador nuevo persistía y el
  // siguiente remount marcaba como vehículos pre-seleccionados. Ahora la key
  // es por-wizard (`${namingPersistForm}_drivers`, llega por prop) y, como
  // defensa extra, un conductor cacheado solo es válido si su vehículo está
  // en la selección actual.
  const getDriversCache = JSON.parse(
    sessionStorage.getItem(driversCacheKey) as string
  );

  const defaultVehicleIds = new Set(
    filterDefaultVehicles.map((vehicle) =>
      String(vehicle.vehicleID || vehicle.id)
    )
  );
  const relevantDriversCache: DriverList = Array.isArray(getDriversCache)
    ? getDriversCache.filter((driver) =>
        defaultVehicleIds.has(String(driver?.vehicleID))
      )
    : [];

  // Tour P5 (2026-07-07): la caché solo manda si tiene entradas — un "[]"
  // persistido pisaba los defaultDrivers rehidratados (mismo fix que en
  // OperatorsAndVehicles/index.tsx).
  const [drivers, setDrivers] = useState<DriverList>(
    relevantDriversCache.length > 0 ? relevantDriversCache : defaultDrivers
  );

  const [operators, setOperators] =
    useState<OperatorsSelectionType[]>(defaultOperators);

  const [vehicles, setVehicles] = useState<VehiclesSelectionType[]>(
    filterDefaultVehicles
  );

  const [changeOperators, setChangeOperators] = useState(false);
  const [changeVehicles, setChangeVehicles] = useState(false);

  // BUG cubo-conductor (2026-07-12): la papelera marca el borrado explícito
  // en el propio form (`vehicleTable[id].driverCleared`) — la marca viaja
  // con el borrador y sobrevive al remount de la pestaña, así los bloques
  // de re-auto-asignado no resucitan un conductor que el usuario borró
  // adrede. Elegir conductor o re-marcar el vehículo limpia la marca.
  const isDriverCleared = (vehicleId: string): boolean =>
    Boolean(
      (
        getValues().vehicleTable?.[vehicleId] as
          | { driverCleared?: boolean }
          | undefined
      )?.driverCleared,
    );

  // Estados para auto-selección visual de equipo en dropdowns (string = nombre del equipo)
  const [selectedOperatorTeam, setSelectedOperatorTeam] = useState<string | undefined>(undefined);
  const [selectedVehicleTeam, setSelectedVehicleTeam] = useState<string | undefined>(undefined);

  // Refs de los desplegables de Conductor, por id de vehículo.
  //
  // Antes: `Array(vehiclesData?.length).fill(null).map(() => useRef(...))` —
  // hooks DENTRO de un bucle cuyo tamaño depende de los datos. Si la lista de
  // vehículos cambiaba de longitud entre renders, React lanzaba «rendered
  // fewer hooks than expected» y se llevaba por delante la pantalla entera;
  // hoy no ocurría solo porque el estado que la alimenta nunca se actualiza.
  // Además se indexaban por `row.index`, que no es la posición de la fila
  // VISIBLE: con la tabla filtrada (el filtro de equipo se aplica solo), la
  // papelera podía limpiar el desplegable de otra fila.
  const dropDownRefs = useRef(new Map<string, ImperativeRefInput | null>());
  const setDropDownRef = useCallback(
    (vehicleId: string) => (el: ImperativeRefInput | null) => {
      if (el) dropDownRefs.current.set(vehicleId, el);
      else dropDownRefs.current.delete(vehicleId);
    },
    [],
  );

  const operatorsColumnHelper = createColumnHelper<OperatorsSelectionType>();
  const vehiclesColumnHelper = createColumnHelper<VehiclesSelectionType>();

  // Dynamic column names with translations
  const translatedOperatorsColumnNames = useMemo(
    () => getOperatorsColumnNames(t),
    [t]
  );
  const translatedVehiclesColumnNames = useMemo(
    () => getVehiclesColumnNames(t),
    [t]
  );
  const handleSelectedOption = (idDriver: string, idVehicle: string) => {
    setVehiclesDataTable((prevData) =>
      prevData.map((row) => {
        return row.id === idVehicle ? { ...row, driver: idDriver } : row;
      })
    );

    // ⚠️ SINCRONIZACIÓN: Disparar evento cuando se asigna conductor
    if (idDriver && idVehicle) {
      dispatchDriverAssigned(idVehicle, idDriver);
      logTablet('Driver assigned event dispatched: vehicle=%s, driver=%s', idVehicle, idDriver);
      liftWorkVehicleExclusion(idVehicle);
    }
  };

  const handleOperatorsOnChange = useCallback(
    (row: Row<OperatorsSelectionType>, key: string) => {
      // Prevention of duplicate events - checking current state
      const currentIsSelected = row.getIsSelected();
      const newWillBeSelected = !currentIsSelected;

      // Detailed log for operator selection diagnosis
      logSelección(
        '🔍 Selección de operador cambiada:',
        JSON.stringify({
          operador: row.original.name + ' ' + row.original.surName,
          id: row.original.id,
          seleccionadoAntes: currentIsSelected,
          seleccionadoDespués: newWillBeSelected,
          índice: row.index,
          keyFormField: key,
        })
      );

      // Marcado EXPLÍCITO al destino calculado (2026-08-08). Antes se llamaba
      // a `row.getToggleSelectedHandler()(row)` pasándole la fila donde
      // TanStack espera un EVENTO: como `row.target` no existe, caía en su
      // rama `value ?? !isSelected` y el destino dependía de un estado que
      // dentro del mismo evento aún no se había actualizado. Con el valor
      // explícito, dos llamadas seguidas (la casilla y la fila) no pueden
      // dejar la selección en un sitio distinto del que decidió el handler.
      row.toggleSelected(newWillBeSelected);

      // Get the current state to compare later
      const operadoresAntesDeActualizar = [...operators];
      logSelección(
        `📊 Operadores ANTES de actualizar (${operadoresAntesDeActualizar.length}):`,
        operadoresAntesDeActualizar
          .map((op) => op.name + ' ' + op.surName)
          .join(', ')
      );

      // Verify current form structure
      const currentFormData = getValues() || {};
      const operatorsTableFromForm =
        currentFormData.operatorsTable || [];

      logSelección(
        `📄 Estado actual del formulario, operatorsTable tiene ${operatorsTableFromForm.length} elementos`
      );

      // Update the internal operator list
      if (newWillBeSelected) {
        logOperación(
          `➕ Añadiendo operador: ${row.original.name} ${row.original.surName}`
        );

        // First update the state, avoiding duplicates
        const operatorAlreadyExists = operators.some(
          (op) => op.id === row.original.id
        );

        if (!operatorAlreadyExists) {
          // Update the state and form with a single update
          const newOperators = [...operators, row.original];
          setOperators(newOperators);

          // Update the form directly (better approach than individual indices)
          setValue('operatorsTable', newOperators);

          logOperación(
            `🧪 setOperators y formulario actualizados: ${newOperators.length} operadores - Se añadió: ${row.original.id}`
          );
        } else {
          logOperación(
            `⚠️ Evitando añadir operador duplicado: ${row.original.name} ${row.original.surName}`
          );
        }
      } else {
        logOperación(
          `➖ Eliminando operador: ${row.original.name} ${row.original.surName}`
        );

        // Remove from local state and update the form in one step
        const newOperators = operators.filter(
          (operator) => operator.id !== row.original.id
        );

        setOperators(newOperators);

        // Update the form directly with the new array
        setValue('operatorsTable', newOperators);

        logOperación(
          `🧪 setOperators y formulario actualizados después de eliminar: ${newOperators.length} operadores - Eliminado: ${row.original.id}`
        );
      }

      // Verify final state after all changes
      logSelección(
        `📊 Operadores DESPUÉS de actualizar (${operators.length}):`,
        operators.map((op) => op.name + ' ' + op.surName).join(', ')
      );
    },
    [setValue, operators, getValues]
  );

  useEffect(() => {
    const operatorsIDs = operators.map((operator) => operator.id);
    logOperación(
      `🔍 useEffect[operators] - Operadores IDs: ${operatorsIDs.join(', ') || ' '
      }`
    );

    // Check synchronization with the form
    const formValues = getValues();
    const formOperators =
      formValues?.operatorsTable?.filter(Boolean) || [];
    const formOperatorIds = formOperators.map(
      (op: OperatorsSelectionType) => op.id
    );

    logOperación(
      `🔍 useEffect[operators] - Operadores en formulario: ${formOperatorIds.join(', ') || 'ninguno'
      } (total: ${formOperators.length})`
    );

    // Detect desynchronization
    if (
      operatorsIDs.length !== formOperatorIds.length ||
      operatorsIDs.some((id) => !formOperatorIds.includes(id)) ||
      formOperatorIds.some((id) => !operatorsIDs.includes(id))
    ) {
      logOperación(
        `⚠️ DESINCRONIZACIÓN detectada entre estado local y formulario`
      );

      // Detail the differences
      const faltanEnForm = operatorsIDs.filter(
        (id) => !formOperatorIds.includes(id)
      );
      const sobranEnForm = formOperatorIds.filter(
        (id) => !operatorsIDs.includes(id)
      );

      if (faltanEnForm.length > 0) {
        logOperación(`⚠️ Faltan en el formulario: ${faltanEnForm.join(', ')}`);
      }

      if (sobranEnForm.length > 0) {
        logOperación(`⚠️ Sobran en el formulario: ${sobranEnForm.join(', ')}`);
      }

      // More robust solution: Use local state as single source of truth
      logOperación(`🧹 Sincronizando formulario con estado local...`);

      // Instead of manipulating specific indices, we update the entire array
      // this avoids type problems and ensures complete synchronization
      setValue('operatorsTable', [...operators]);

      logOperación(
        `✅ Sincronización completada - Formulario actualizado con ${operators.length} operadores`
      );
    } else {
      logOperación(`✅ Estado local y formulario sincronizados correctamente`);
    }

    const removedDrivers = drivers.filter(
      (driver) => driver && !operatorsIDs.includes(driver.driverID)
    );

    logOperación(
      `🔍 useEffect[operators] - Conductores a resetear: ${removedDrivers.length}`
    );

    // El guardado (saveData create/edit) lee `vehicleTable[vehicleID].driverID`
    // — objeto por id de vehículo, no el array `vehiclesTable` por índice. Si
    // no se limpia aquí, el conductor eliminado se re-envía en
    // SAVE_ACTION.input.vehicles y reaparece en el resumen tras guardar.
    removedDrivers.forEach(
      (driver) =>
        isInternal &&
        setValue(
          `vehicleTable[${driver.vehicleID}].driverID` as keyof OperatorsAndVehiclesFormValues,
          undefined
        )
    );

    // Tour P5 (2026-07-07, feedback usuario): al quitar un operario que era
    // conductor solo se limpiaba vehiclesTable[index] y el array visual
    // `drivers` — el mapa vehicleTable[vehicleId].driverID (la fuente del
    // payload y de la validación de tripulación) conservaba el conductor
    // FANTASMA: el dropdown mostraba "Seleccionar operario" pero los datos
    // decían que había conductor y la validación no saltaba.
    drivers.forEach(
      (driver) =>
        driver &&
        !operatorsIDs.includes(driver.driverID) &&
        isInternal &&
        setValue(
          `vehicleTable[${driver.vehicleID}].driverID` as keyof OperatorsAndVehiclesFormValues,
          undefined
        )
    );

    logOperación(
      `🔍 useEffect[operators] - Llamando a setDefaultOperators con ${operators.length} operarios`
    );

    setDefaultOperators(operators);

    logOperación(`🔍 useEffect[operators] - Actualizando lista de conductores`);
    const updatedDrivers: DriverList = [];
    drivers.forEach(
      (driver) =>
        operatorsIDs.includes(driver.driverID) && updatedDrivers.push(driver)
    );

    // Tour P5 (2026-07-07, feedback usuario): re-auto-asignar conductor a
    // los vehículos seleccionados que queden SIN él (espejo del auto-
    // asignado al marcar el vehículo: primer operario seleccionado). Cubre
    // el flujo quitar-operario → volver a marcarlo con el vehículo ya
    // seleccionado, que dejaba el vehículo sin conductor visible.
    if (isInternal && operators.length > 0) {
      const fallbackDriverId = operators[0].id;
      const coveredVehicleIds = new Set(
        updatedDrivers.map((driver) => driver.vehicleID)
      );
      vehicles.forEach((vehicle) => {
        const vehicleId = vehicle.vehicleID || vehicle.id;
        if (!vehicleId || coveredVehicleIds.has(vehicleId)) return;
        // BUG cubo-conductor (2026-07-12): borrado explícito con la
        // papelera (marca persistida en el borrador) — no resucitarlo.
        if (isDriverCleared(vehicleId)) return;
        setValue(
          `vehicleTable[${vehicleId}].vehicleID` as keyof OperatorsAndVehiclesFormValues,
          vehicleId
        );
        setValue(
          `vehicleTable[${vehicleId}].driverID` as keyof OperatorsAndVehiclesFormValues,
          fallbackDriverId
        );
        updatedDrivers.push({
          vehicleID: vehicleId,
          driverID: fallbackDriverId,
        });
        logOperación(
          `👤 Re-auto-asignando conductor ${fallbackDriverId} al vehículo ${vehicleId}`
        );
      });
    }

    isInternal && setDrivers(updatedDrivers);

    logOperación(
      `📊 useEffect[operators] finalizado - Operadores: ${operators.length}, Conductores actualizados: ${updatedDrivers.length}`
    );
  }, [operators]);

  useEffect(() => {
    setDefaultVehicles(vehicles);
  }, [vehicles]);
  const handleOnChange = useCallback(
    (row: Row<VehiclesSelectionType>) => {
      // Improved vehicle management - detailed log
      logVehicles(
        '🚗 Cambio en selección de vehículo:',
        JSON.stringify({
          placa: row.original.plate,
          id: row.original.id,
          seleccionado: !row.getIsSelected(),
          conductorActual: row.original.driver,
        })
      );

      // Destino calculado UNA vez, antes de tocar nada (2026-08-08). El código
      // anterior marcaba primero y luego leía `row.getIsSelected()` contando
      // con que devolviese el valor ANTERIOR (dentro del mismo evento la
      // selección aún no se ha re-renderizado), lo que hacía ilegible cada
      // rama de este handler.
      const willBeSelected = !row.getIsSelected();

      // Sin operarios no se puede marcar vehículo: se comprueba ANTES de tocar
      // la selección. El código anterior marcaba y luego intentaba "revertir"
      // llamando otra vez al toggle de TanStack, que calcula el destino con el
      // `isSelected` de antes → volvía a poner lo mismo: el vehículo se
      // quedaba MARCADO en pantalla y FUERA de la tripulación, y hacían falta
      // tres toques para meterlo de verdad.
      if (willBeSelected && operators.length === 0) {
        logVehicles(
          '⚠️ Intento de añadir vehículo sin operadores seleccionados'
        );
        return;
      }

      // Marcado explícito al destino (mismo motivo que en los operarios).
      row.toggleSelected(willBeSelected);

      if (willBeSelected) {
        // If it already exists, don't add it again
        const vehicleExists = vehicles.some(
          (v) => v.vehicleID === row.original.id || v.id === row.original.id
        );

        if (vehicleExists) {
          logVehicles(
            '⚠️ Evitando añadir vehículo duplicado:',
            row.original.plate
          );
          return;
        }

        // Ensure the vehicle has a driver
        const vehicleWithDriver = {
          ...row.original,
          // Store both id and vehicleID to ensure it's not lost
          id: row.original.id,
          vehicleID: row.original.id,
          // Use the first selected operator as the default driver
          driverID: operators[0]?.id || drivers[row.index]?.driverID,
          driver: operators[0]?.id || drivers[row.index]?.driverID,
        };

        logVehicles(
          '➕ Añadiendo vehículo con conductor:',
          JSON.stringify({
            placa: vehicleWithDriver.plate,
            vehicleID: vehicleWithDriver.vehicleID,
            conductorID: vehicleWithDriver.driverID || vehicleWithDriver.driver,
          })
        );

        // Update vehicles state
        const newVehicles = [...vehicles, vehicleWithDriver];
        setVehicles(newVehicles);

        // If we're adding a vehicle, ensure it has a driver
        if (operators.length > 0) {
          const driverId = operators[0]?.id;
          // Marcarlo es elegirlo otra vez: si se había quitado en Maquinaria,
          // vuelve a precargarse allí.
          liftWorkVehicleExclusion(row.original.id);

          // It's crucial to set both fields - vehicleID and driverID
          setValue(
            `vehicleTable[${row.original.id}].vehicleID` as keyof OperatorsAndVehiclesFormValues,
            row.original.id
          );

          setValue(
            `vehicleTable[${row.original.id}].driverID` as keyof OperatorsAndVehiclesFormValues,
            driverId
          );

          // Re-marcar el vehículo deshace el borrado explícito de la
          // papelera (BUG cubo-conductor 2026-07-12).
          setValue(
            `vehicleTable[${row.original.id}].driverCleared` as keyof OperatorsAndVehiclesFormValues,
            undefined
          );

          // ✅ FIX: También actualizar el array drivers para sincronizar el dropdown visualmente
          setDrivers((prevDrivers) => {
            const newDrivers = [...prevDrivers];
            // Buscar si ya existe un driver para este vehículo
            const existingIndex = newDrivers.findIndex(
              (driver) => driver.vehicleID === row.original.id
            );

            if (existingIndex === -1) {
              // No existe, añadir nuevo
              newDrivers.push({
                vehicleID: row.original.id,
                driverID: driverId,
              });
            } else {
              // Ya existe, actualizar el driverID
              newDrivers[existingIndex].driverID = driverId;
            }

            logVehicles(
              '🔄 Drivers array actualizado para sincronizar dropdown:',
              JSON.stringify(newDrivers)
            );

            return newDrivers;
          });

          logVehicles(
            '👤 Asignando conductor al vehículo:',
            JSON.stringify({
              vehiculoID: row.original.id,
              conductorID: driverId,
            })
          );
        }
      } else {
        // Remove the vehicle
        setVehicles((prevVehicles) =>
          prevVehicles
            .filter((vehicle) => vehicle.vehicleID !== row.original.id)
            .filter((vehicle) => vehicle.id !== row.original.id)
        );

        // ✅ FIX: También limpiar el array drivers para sincronizar el dropdown
        setDrivers((prevDrivers) =>
          prevDrivers.filter((driver) => driver.vehicleID !== row.original.id)
        );

        logVehicles('➖ Quitando vehículo:', row.original.plate);

        // Clear the form - first set both values to undefined
        setValue(
          `vehicleTable[${row.original.id}].vehicleID` as keyof OperatorsAndVehiclesFormValues,
          undefined
        );

        setValue(
          `vehicleTable[${row.original.id}].driverID` as keyof OperatorsAndVehiclesFormValues,
          undefined
        );

        // Also clear the complete entry
        setValue(
          `vehicleTable[${row.original.id}]` as keyof OperatorsAndVehiclesFormValues,
          undefined
        );
      }
    },
    [operators, vehicles, drivers, setValue]
  );
  const operatorsColumns = useMemo(
    () => [
      {
        enableHiding: true,
        id: 'select',
        cell: ({ row }: { row: Row<OperatorsSelectionType> }) => {
          return (
            <Controller
              name={
                `${tableName}.${row.index}` as keyof NewAction &
                  OperatorsAndVehiclesFormValues
              }
              control={control}
              defaultValue={defaultOperators[row.index]}
              render={() => {
                return (
                  <CheckBoxStyled
                    checked={row.getIsSelected()}
                    indeterminate={!row.getCanSelect()}
                    disabled={
                      data?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      data?.parentExpeditionOrderStatus === OrderState.End ||
                      data?.state === OrderState.Annulled
                    }
                    onClick={(e) => {
                      // Un toque = UNA decisión (2026-08-08). La casilla vive
                      // dentro de una `<Tr onClick>` que llama al mismo
                      // handler, así que tocar la casilla lo ejecutaba DOS
                      // veces por toque. Hoy ambas llamadas calculan el mismo
                      // destino y se anulan, pero basta un render intercalado
                      // para que la segunda deshaga a la primera y el toque no
                      // marque nada. Mismo corte que ya hace la papelera.
                      e.stopPropagation();
                    }}
                    onChange={() => {
                      handleOperatorsOnChange(row, `${tableName}.${row.index}`);
                    }}
                  />
                );
              }}
            />
          );
        },
      },
      operatorsColumnHelper.accessor((row) => row.name, {
        id: translatedOperatorsColumnNames.name.id,
        header: () => translatedOperatorsColumnNames.name.label,
        cell: (info) => info.getValue(),
        // El buscador filtra por esta columna: que mire nombre Y apellidos,
        // sin acentos y por palabras («anna roura»). Antes, solo el nombre y
        // literal.
        // Nombre y apellidos JUNTOS: `matchesSearch` exige todas las palabras en
        // un mismo campo.
        filterFn: textSearchFilter((row) => [`${row.name ?? ''} ${row.surName ?? ''}`]),
      }),
      operatorsColumnHelper.accessor((row) => row.surName, {
        id: translatedOperatorsColumnNames.surName.id,
        header: () => translatedOperatorsColumnNames.surName.label,
      }),
      operatorsColumnHelper.accessor((row) => row.concession, {
        id: translatedOperatorsColumnNames.concession.id,
        header: () => translatedOperatorsColumnNames.concession.label,
        filterFn: 'arrIncludes',
      }),
      operatorsColumnHelper.accessor((row) => row.isActive, {
        id: translatedOperatorsColumnNames.isActive.id,
        header: () => translatedOperatorsColumnNames.isActive.label,
      }),
      // Hidden column for team filtering
      operatorsColumnHelper.accessor((row) => row.team, {
        id: translatedOperatorsColumnNames.team.id,
        header: () => translatedOperatorsColumnNames.team.label,
        enableHiding: true,
      }),
    ],
    [drivers, changeOperators, translatedOperatorsColumnNames]
  );

  const vehiclesColumns = useMemo(
    () => [
      {
        id: 'select',
        cell: ({ row }: { row: Row<VehiclesSelectionType> }) => {
          return (
            <Controller
              name={
                `vehicleTable[${row.original.id}]` as keyof OperatorsAndVehiclesFormValues
              }
              control={control}
              defaultValue={undefined}
              render={() => {
                return (
                  <CheckBoxStyled
                    checked={row.getIsSelected()}
                    indeterminate={!row.getCanSelect()}
                    disabled={
                      data?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      data?.parentExpeditionOrderStatus === OrderState.End ||
                      data?.state === OrderState.Annulled
                    }
                    onClick={(e) => {
                      // Un toque = UNA decisión (2026-08-08). La casilla vive
                      // dentro de una `<Tr onClick>` que llama al mismo
                      // handler, así que tocar la casilla lo ejecutaba DOS
                      // veces por toque. Hoy ambas llamadas calculan el mismo
                      // destino y se anulan, pero basta un render intercalado
                      // para que la segunda deshaga a la primera y el toque no
                      // marque nada. Mismo corte que ya hace la papelera.
                      e.stopPropagation();
                    }}
                    onChange={() => {
                      handleOnChange(row);
                    }}
                  />
                );
              }}
            />
          );
        },
      },
      vehiclesColumnHelper.accessor((row) => row.plate, {
        id: translatedVehiclesColumnNames.plate.id,
        header: () => translatedVehiclesColumnNames.plate.label,
        // Matrícula con o sin guion («1234abc» encuentra «1234-ABC»).
        filterFn: textSearchFilter(),
      }),
      vehiclesColumnHelper.accessor((row) => row.category, {
        id: translatedVehiclesColumnNames.category.id,
        header: () => translatedVehiclesColumnNames.category.label,
        cell: (info) => info.getValue() || '-',
      }),
      vehiclesColumnHelper.accessor((row) => row.concession, {
        id: translatedVehiclesColumnNames.concession.id,
        header: () => translatedVehiclesColumnNames.concession.label,
      }),
      vehiclesColumnHelper.accessor((row) => row.driver, {
        id: translatedVehiclesColumnNames.driver.id,
        header: () => translatedVehiclesColumnNames.driver.label,
        cell: ({ row }) => {
          const options: Options = operatorsTable
            .getSelectedRowModel()
            .rows.map((row) => ({
              label: row.original.surName,
              value: row.original.id,
            }));

          // Define the variable outside the if block to have it in scope
          let operatorsSelectedOptions: Options = [];

          const cacheOperatorsSelectedRaw = sessionStorage.getItem(
            formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM
          );
          if (cacheOperatorsSelectedRaw) {
            const cacheOperatorsSelected = JSON.parse(
              decompressSessionStorage(cacheOperatorsSelectedRaw)
            );
            operatorsSelectedOptions =
              cacheOperatorsSelected?.operatorsTable?.reduce(
                (acc: Options, prev: OperatorsSelectionType) => {
                  if (prev !== null) {
                    const operator = {
                      value: prev.id,
                      label: prev.surName,
                    };
                    acc.push(operator);
                  }
                  return acc;
                },
                [] as Options
              ) || [];
          }

          const driverFind = drivers.find(
            (driver) => driver.vehicleID === row.original.id
          );

          return (
            <ContainerInput>
              <Controller
                name={
                  `vehicleTable[${row.original.id}]` as keyof OperatorsAndVehiclesFormValues
                }
                control={control}
                render={() => {
                  return (
                    <DropDownInputStyled
                      // ✅ FIX: Key dinámica para re-renderizar cuando cambia el conductor auto-asignado
                      key={`driver-dropdown-${row.original.id}-${driverFind?.driverID || 'none'}`}
                      isDisabled={
                        data?.parentExpeditionOrderStatus ===
                          OrderState.Annulled ||
                        data?.parentExpeditionOrderStatus === OrderState.End ||
                        data?.state === OrderState.Annulled ||
                        // Puerta lateral cerrada (2026-08-08): el desplegable se
                        // pinta en TODAS las filas, y elegir conductor en una NO
                        // marcada escribía `vehicleTable[id].driverID` sin marcar
                        // el vehículo. Ese resto se contaba después como vehículo
                        // seleccionado y acababa en el recorrido sin que nadie lo
                        // hubiera marcado. El flujo bueno no cambia: marcas el
                        // vehículo → se auto-asigna el primer operario → aquí lo
                        // cambias.
                        !row.getIsSelected()
                      }
                      defaultValue={driverFind?.driverID}
                      ref={setDropDownRef(row.original.id)}
                      inputSize="XL"
                      placeholder={t('placeholders.selectOperator')}
                      typeDropDown={'Table'}
                      options={isInternal ? options : operatorsSelectedOptions}
                      handleSelectedOption={(value) => {
                        setDrivers((prevDrivers) => {
                          const newDrivers = [...prevDrivers];
                          let includesIDValue = false;
                          let indexID;
                          newDrivers.forEach((driver, index) => {
                            if (driver.vehicleID === row.original.id) {
                              includesIDValue = true;
                              indexID = index;
                            }
                          });

                          if (!includesIDValue) {
                            newDrivers.push({
                              vehicleID: row.original.id,
                              driverID: value as string,
                            });
                          } else {
                            if (indexID || indexID === 0)
                              newDrivers[indexID].driverID = value as string;
                          }

                          return newDrivers;
                        });

                        setValue(
                          `vehicleTable[${row.original.id}].driverID` as keyof OperatorsAndVehiclesFormValues,
                          value as string
                        );
                        // Elegir conductor deshace el borrado explícito de
                        // la papelera (BUG cubo-conductor 2026-07-12).
                        setValue(
                          `vehicleTable[${row.original.id}].driverCleared` as keyof OperatorsAndVehiclesFormValues,
                          undefined
                        );
                        handleSelectedOption(value as string, row.original.id);
                      }}
                    />
                  );
                }}
              />
            </ContainerInput>
          );
        },
      }),
      vehiclesColumnHelper.accessor((row) => row.remove, {
        id: translatedVehiclesColumnNames.remove.id,
        header: () => (
          <Typography colorText="white" size="S">
            -
          </Typography>
        ),
        cell: ({ row }) => (
          <Img
            src={RemoveIcon}
            onClick={(e) => {
              e.stopPropagation();
              dropDownRefs.current.get(row.original.id)?.clearSelected();

              setDrivers((prevDrivers) => {
                const newDrivers = [...prevDrivers];
                const driverFilter = newDrivers.filter(
                  (driver) => driver.vehicleID !== row.original.id
                );
                return driverFilter;
              });
              setValue(
                `vehicleTable[${row.original.id}].driverID` as keyof OperatorsAndVehiclesFormValues,
                undefined
              );
              // BUG cubo-conductor (2026-07-12): marca el borrado explícito
              // (viaja con el borrador) para que el re-auto-asignado no
              // resucite al conductor al volver a montar la pestaña.
              setValue(
                `vehicleTable[${row.original.id}].driverCleared` as keyof OperatorsAndVehiclesFormValues,
                true
              );

              if (
                getValues().vehicleTable[row.original.id].vehicleID ===
                  undefined &&
                getValues().vehicleTable[row.original.id].driverID === undefined
              ) {
                setValue(
                  `vehicleTable[${row.original.id}]` as keyof OperatorsAndVehiclesFormValues,
                  undefined
                );
              }
            }}
          />
        ),
      }),
      vehiclesColumnHelper.accessor((row) => row.isActive, {
        id: translatedVehiclesColumnNames.isActive.id,
        header: () => translatedVehiclesColumnNames.isActive.label,
      }),
      // Hidden column for team filtering - ✅ M2M: now handles array of teams
      vehiclesColumnHelper.accessor((row) => row.teams, {
        id: translatedVehiclesColumnNames.team.id,
        header: () => translatedVehiclesColumnNames.team.label,
        enableHiding: true,
        // ✅ M2M: Custom filterFn to match team names in array
        filterFn: (row, columnId, filterValue) => {
          const teams = row.getValue(columnId) as string[] | undefined;
          if (!teams || teams.length === 0) return false;

          // If filterValue is a string, check if team name is in the array
          if (typeof filterValue === 'string') {
            return teams.includes(filterValue);
          }

          // If filterValue is an array, check if any team matches
          if (Array.isArray(filterValue)) {
            return filterValue.some((teamName) => teams.includes(teamName));
          }

          return false;
        },
      }),
    ],
    [
      drivers,
      operators,
      changeVehicles,
      vehicles,
      translatedVehiclesColumnNames,
    ]
  );

  const operatorsHandleChange = useCallback<handleChangeTypes>(
    (value) => {
      operatorsTable.getColumn('firstName')?.setFilterValue(value);
      value === '' && setChangeOperators((prev) => !prev);
    },
    [changeOperators]
  );

  const vehiclesHandleChange = useCallback<handleChangeTypes>(
    (value) => {
      vehiclesTable.getColumn('plate')?.setFilterValue(value);
      value === '' && setChangeVehicles((prev) => !prev);
    },
    [changeVehicles]
  );

  const defaultConcession = initialData?.concessions?.filter(
    (con) => con.id === initialData?.concession
  )?.[0]?.name;

  const concessionForAllOperators = data?.breadCrumbsData?.concessions?.filter(
    (concession) => concession.id === ''
  )?.[0]?.name;

  const options =
    initialData?.concessions?.reduce((acc, curr) => {
      acc.push({
        label: curr?.name || '',
        value: curr?.id || '',
      });

      return acc;
    }, [] as GroupTypes[]) || [];

  const onSelectConcession = useCallback(
    (option: OptionSelected | undefined) => {
      if (option?.value !== '') {
        operatorsTable.getColumn('concession')?.setFilterValue(option?.label);
      } else {
        operatorsTable.getColumn('concession')?.setFilterValue(undefined);
      }
    },
    []
  );

  const onSelectState = useCallback((option: OptionSelected | undefined) => {
    if (option?.value === 'true') {
      operatorsTable.getColumn('isActive')?.setFilterValue(true);
    } else if (option?.value === 'false') {
      operatorsTable.getColumn('isActive')?.setFilterValue(false);
    } else {
      operatorsTable.getColumn('isActive')?.setFilterValue(undefined);
    }
  }, []);

  // Function to handle filtering by state in vehicles (Active/Inactive/All)
  const onSelectStateVehicle = useCallback(
    (option: OptionSelected | undefined) => {
      if (option?.value === 'true') {
        vehiclesTable.getColumn('isActive')?.setFilterValue(true);
      } else if (option?.value === 'false') {
        vehiclesTable.getColumn('isActive')?.setFilterValue(false);
      } else {
        // Only reset the isActive filter, not all filters
        vehiclesTable.getColumn('isActive')?.setFilterValue(undefined);
      }
    },
    []
  );

  // Function to handle filtering by concession in vehicles
  const onSelectVehicleConcession = useCallback(
    (option: OptionSelected | undefined) => {
      if (option?.value === '') {
        // Only reset the concession filter, not all filters
        vehiclesTable.getColumn('concession')?.setFilterValue(undefined);
      } else {
        const concession = concessions.find(
          (concession) => concession.name === option?.value
        );

        vehiclesTable.getColumn('concession')?.setFilterValue(concession?.name);
      }
    },
    []
  );

  // Function to handle filtering by team in operators
  const onSelectTeam = useCallback(
    (option: OptionSelected | undefined) => {
      // ✅ Actualizar estado visual del dropdown (almacenar nombre del equipo)
      setSelectedOperatorTeam(option?.value === '' ? undefined : option?.label);
      if (option?.value === '') {
        operatorsTable.getColumn('team')?.setFilterValue(undefined);
      } else {
        operatorsTable.getColumn('team')?.setFilterValue(option?.label);
      }
    },
    []
  );

  // Function to handle filtering by team in vehicles
  const onSelectVehicleTeam = useCallback(
    (option: OptionSelected | undefined) => {
      // ✅ Actualizar estado visual del dropdown (almacenar nombre del equipo)
      setSelectedVehicleTeam(option?.value === '' ? undefined : option?.label);
      if (option?.value === '') {
        vehiclesTable.getColumn('team')?.setFilterValue(undefined);
      } else {
        vehiclesTable.getColumn('team')?.setFilterValue(option?.label);
      }
    },
    []
  );

  const operatorsTable = useReactTable({
    data: operatorsDataAll,
    columns: operatorsColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      columnVisibility: {
        [translatedOperatorsColumnNames.isActive.id]: false,
        [translatedOperatorsColumnNames.team.id]: false, // Hidden team column for filtering
        // Ocultar columna concesión si solo hay una
        ...(concessions.length === 1 && {
          [translatedOperatorsColumnNames.concession.id]: false,
        }),
      },
    },
  });

  const vehiclesTable = useReactTable({
    data: vehiclesDataTable,
    columns: vehiclesColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      columnVisibility: {
        [translatedVehiclesColumnNames.isActive.id]: false,
        [translatedVehiclesColumnNames.team.id]: false, // Hidden team column for filtering
        // Ocultar columna concesión si solo hay una
        ...(concessions.length === 1 && {
          [translatedVehiclesColumnNames.concession.id]: false,
        }),
      },
    },
  });

  useEffect(() => {
    data?.flowData !== 'validation' ||
      (!data.flowData &&
        operatorsTable
          .getColumn('concession')
          ?.setFilterValue(defaultConcession));

    // Si solo hay una concesión, aplicar filtro automáticamente (por nombre)
    if (concessions.length === 1) {
      operatorsTable
        .getColumn('concession')
        ?.setFilterValue(concessions[0].name);
    }

    setDrivers(drivers.map((element) => element));
  }, []);

  useEffect(() => {
    setCacheDrivers && setCacheDrivers(drivers);
  }, [drivers]);

  // Aplicar filtro automático de concesión de vehículos cuando solo hay una
  useEffect(() => {
    if (concessions.length === 1) {
      vehiclesTable
        .getColumn('concession')
        ?.setFilterValue(concessions[0].name);
    }
  }, [concessions]);

  // Auto-selección de equipo para usuarios con rol Tablet/OperatingTeam
  useEffect(() => {
    // ✅ M2M: Get first team from teamIDs array for auto-filter
    const currentUserTeamId = user?.user?.teamIDs?.[0];
    const currentUserRole = user?.user?.rolActive;

    // 🔬 DEBUG - Diagnóstico rol Tablet
    logTablet('Auto-filtro Tablet: %O', {
      rol: currentUserRole,
      teamID_usuario: currentUserTeamId || 'NO TIENE',
      teams_recibidos: teams?.length || 0,
      teams_nombres: teams?.map((t) => t.name) || [],
    });

    // Solo auto-seleccionar si el usuario tiene equipo y es Tablet/OperatingTeam
    const rolesWithTeam = [Role.Tablet, Role.OperatingTeam];
    if (
      currentUserTeamId &&
      rolesWithTeam.includes(currentUserRole as Role) &&
      teams &&
      teams.length > 0
    ) {
      const userTeam = teams.find((t) => t.id === currentUserTeamId);

      // 🔬 DEBUG - Resultado búsqueda equipo
      logTablet('Buscando equipo: %O', {
        teamID_buscado: currentUserTeamId,
        equipo_encontrado: userTeam ? userTeam.name : 'NO ENCONTRADO',
        filtro_aplicado: !!userTeam,
      });

      if (userTeam) {
        operatorsTable.getColumn('team')?.setFilterValue(userTeam.name);
        vehiclesTable.getColumn('team')?.setFilterValue(userTeam.name);

        // ✅ Actualizar estado visual del dropdown para mostrar equipo seleccionado
        setSelectedOperatorTeam(userTeam.name);
        setSelectedVehicleTeam(userTeam.name);
      }
    } else {
      // 🔬 DEBUG - Por qué no se aplica filtro
      logTablet('Filtro NO aplicado: %O', {
        razon: !currentUserTeamId ? 'Usuario sin teamID' :
          !rolesWithTeam.includes(currentUserRole as Role) ? 'Rol no es Tablet/OperatingTeam' :
            !teams || teams.length === 0 ? '⚠️ TEAMS VACÍO' : 'Desconocida',
      });
    }
  }, [teams, user?.user?.teamIDs, user?.user?.rolActive]);

  // V4 FR-3.2 (audit Disc#10, 2026-05-28): si solo hay UN equipo disponible
  // (caso Recorregut de Vigilància: el loader filtra a `isSurveillance` →
  // típicamente solo ÈXSER), auto-aplicar el filtro de ese equipo a ambas
  // tablas. El dropdown del filtro ya se oculta solo (render condicionado a
  // `teams.length > 1`, igual que el selector de concesión). Así la tabla
  // muestra solo operarios/vehículos de ese único equipo sin que el usuario
  // toque nada — mismo patrón de autoselección que las concesiones.
  // Independiente del rol (a diferencia del useEffect Tablet de arriba).
  // En work-orders (varios equipos en el tenant) la condición no se cumple,
  // así que no altera ese flujo.
  useEffect(() => {
    if (teams && teams.length === 1) {
      const onlyTeam = teams[0];
      operatorsTable.getColumn('team')?.setFilterValue(onlyTeam.name);
      vehiclesTable.getColumn('team')?.setFilterValue(onlyTeam.name);
      setSelectedOperatorTeam(onlyTeam.name);
      setSelectedVehicleTeam(onlyTeam.name);
    }
  }, [teams]);

  useEffect(() => {
    const operatorsIDs = operators.map((operator) => operator.id);
    // `driver` es el fallback legacy que saveData también lee
    // (`vehicle.driverID || vehicle.driver`) — limpiar ambos campos.
    const removedDrivers = drivers.filter(
      (driver) => driver && !operatorsIDs.includes(driver.driverID)
    );
    removedDrivers.forEach(
      (driver) =>
        isInternal &&
        setValue(
          `vehicleTable[${driver.vehicleID}].driver` as keyof OperatorsAndVehiclesFormValues,
          undefined
        )
    );
    setDefaultOperators(operators);

    const updatedDrivers: DriverList = [];
    drivers.forEach(
      (driver) =>
        operatorsIDs.includes(driver.driverID) && updatedDrivers.push(driver)
    );

    // Tour P5 (2026-07-07, feedback usuario): este efecto duplica al de
    // arriba (deps [operators]) y corre DESPUÉS — sin este espejo pisaba el
    // re-auto-asignado con un setDrivers([]) del closure viejo y el
    // dropdown volvía a "Seleccionar operario". Misma lógica: limpiar el
    // conductor fantasma de vehicleTable y re-auto-asignar el primer
    // operario a los vehículos seleccionados sin conductor.
    drivers.forEach(
      (driver) =>
        driver &&
        !operatorsIDs.includes(driver.driverID) &&
        isInternal &&
        setValue(
          `vehicleTable[${driver.vehicleID}].driverID` as keyof OperatorsAndVehiclesFormValues,
          undefined
        )
    );
    if (isInternal && operators.length > 0) {
      const fallbackDriverId = operators[0].id;
      const coveredVehicleIds = new Set(
        updatedDrivers.map((driver) => driver.vehicleID)
      );
      vehicles.forEach((vehicle) => {
        const vehicleId = vehicle.vehicleID || vehicle.id;
        if (!vehicleId || coveredVehicleIds.has(vehicleId)) return;
        // BUG cubo-conductor (2026-07-12): borrado explícito con la
        // papelera (marca persistida en el borrador) — no resucitarlo.
        if (isDriverCleared(vehicleId)) return;
        setValue(
          `vehicleTable[${vehicleId}].vehicleID` as keyof OperatorsAndVehiclesFormValues,
          vehicleId
        );
        setValue(
          `vehicleTable[${vehicleId}].driverID` as keyof OperatorsAndVehiclesFormValues,
          fallbackDriverId
        );
        updatedDrivers.push({
          vehicleID: vehicleId,
          driverID: fallbackDriverId,
        });
      });
    }

    isInternal && setDrivers(updatedDrivers);

    setDefaultVehicles(vehicles);
  }, [operators, vehicles]);

  // To diagnose possible duplicate renders
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
    logOperación(
      `🔢 RENDER #${renderCount.current} - Operadores: ${operators.length}, Vehículos: ${vehicles.length}`
    );
    return () => {
      logOperación(`🧹 CLEANUP RENDER #${renderCount.current}`);
    };
  });

  useLayoutEffect(() => {
    logEstado('📊 useLayoutEffect - Inicializando selecciones de tabla');
    logEstado(`📊 Operadores por defecto: ${defaultOperators.length}`);

    // Get the operators that are actually in the form
    const tableValues = getValues().operatorsTable;
    const validFormOperators = tableValues ? tableValues.filter(Boolean) : [];

    logEstado(
      `📊 Operadores en formulario antes de init: ${validFormOperators.length}`
    );

    // If there are operators in the form, update the local state
    if (validFormOperators.length > 0) {
      // Only update if the length is different to avoid loops
      if (operators.length !== validFormOperators.length) {
        logEstado(`🔄 Actualizando estado local con operadores del formulario`);
        setOperators(validFormOperators);
      }
    }

    operatorsTable.getRowModel().rows.forEach((row) => {
      // Use the form operators to decide the initial selection
      const isSelected =
        validFormOperators.some((op) => op.id === row.original.id) ||
        defaultOperators.some((element) => element.id === row.original.id);

      logEstado(
        `${isSelected ? '✅' : '❌'} Toggle operador ${row.original.name
        } a ${isSelected}`
      );

      row.toggleSelected(isSelected);
    });

    vehiclesTable.getRowModel().rows.forEach((row) => {
      const wasSelected =
        vehicles.some((element) => element.vehicleID === row.original.id) ||
        vehicles.some((element) => element.id === row.original.id);
      logEstado(
        `${wasSelected ? '✅' : '❌'} Toggle vehículo ${row.original.plate
        } a ${wasSelected}`
      );
      row.toggleSelected(wasSelected);
    }, []);

    logEstado('📊 useLayoutEffect finalizado');
  }, []);

  const path = location.pathname.split('/');
  const isValidation = path.includes('actions-validation');
  // Addendum RV 2026-07-20: señal de contexto opcional (undefined en
  // Actuaciones → comportamiento intacto, `isValidation` sigue siendo la
  // única señal). La edición del RV pasa `showCrewStateFilter=true`.
  const showStateFilter = isValidation || showCrewStateFilter;

  return (
    <ContainerTable>
      <OperatorsTableContainer>
        <Typography size="L" variant="semiBold">
          {titles && titles[0]}
        </Typography>
        <FiltersContainer>
          {/* Selector de concesión - Solo se muestra si hay más de una concesión */}
          {concessions.length > 1 && (
            <DropDownFilterStyled
              inputSize={'XS'}
              onChangeSelected={(option) => onSelectConcession(option)}
              typeDropDown="Default"
              placeholder={t('labels.select')}
              defaultValue={
                data?.flowData === 'validation'
                  ? concessionForAllOperators
                  : defaultConcession
              }
              options={options}
            />
          )}
          {/* Selector de equipo - Solo se muestra si hay más de un equipo */}
          {teams && teams.length > 1 && (
            <DropDownFilterStyled
              // ✅ FIX BUG #30: Key dinámica para forzar re-mount cuando cambia el equipo auto-seleccionado
              key={`operator-team-filter-${selectedOperatorTeam || 'all'}`}
              inputSize={'XS'}
              onChangeSelected={(option) => onSelectTeam(option)}
              typeDropDown="Default"
              placeholder={t('forms:operators.team', 'Equipo')}
              defaultValue={selectedOperatorTeam}
              options={[
                {
                  label: t('filters.all'),
                  value: '',
                },
                ...teams.map((team) => ({
                  label: team.name,
                  value: team.id,
                })),
              ]}
            />
          )}
          {showStateFilter && (
            <DropDownFilterStyled
              inputSize={'XS'}
              onChangeSelected={(option) => onSelectState(option)}
              typeDropDown="Default"
              placeholder={t('labels.select')}
              options={[
                {
                  label: t('filters.all'),
                  value: '',
                },
                {
                  label: t('filters.active'),
                  value: 'true',
                },
                {
                  label: t('filters.inactive'),
                  value: 'false',
                },
              ]}
            />
          )}
          <SearchBar
            variant="form"
            onChangeInput={(e) => operatorsHandleChange(e.target.value)}
          ></SearchBar>
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {operatorsTable.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableLeftTh
                      key={header.id}
                      cell={header.id as variantLeftTh}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableLeftTh>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {operatorsTable.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  onClick={() => {
                    if (
                      data?.parentExpeditionOrderStatus !==
                        OrderState.Annulled &&
                      data?.parentExpeditionOrderStatus !== OrderState.End &&
                      data?.state !== OrderState.Annulled
                    ) {
                      // Manejo directo del cambio para mejor respuesta
                      handleOperatorsOnChange(row, `${tableName}.${row.index}`);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </OperatorsTableContainer>
      <VehiclesTableContainer>
        <Typography size="L" variant="semiBold">
          {titles && titles[1]}
        </Typography>
        <FiltersContainer>
          {/* Selector de concesión de vehículos - Solo se muestra si hay más de una concesión */}
          {concessions.length > 1 && (
            <DropDownFilterStyled
              inputSize={'XS'}
              onChangeSelected={(option) => onSelectVehicleConcession(option)}
              typeDropDown="Default"
              placeholder={t('labels.select')}
              options={[
                {
                  label: t('filters.all'),
                  value: '',
                },
                ...concessions.map((concession) => ({
                  label: concession.name,
                  value: concession.name,
                })),
              ]}
            />
          )}
          {/* Selector de equipo de vehículos - Solo se muestra si hay más de un equipo */}
          {teams && teams.length > 1 && (
            <DropDownFilterStyled
              // ✅ FIX BUG #30: Key dinámica para forzar re-mount cuando cambia el equipo auto-seleccionado
              key={`vehicle-team-filter-${selectedVehicleTeam || 'all'}`}
              inputSize={'XS'}
              onChangeSelected={(option) => onSelectVehicleTeam(option)}
              typeDropDown="Default"
              placeholder={t('forms:vehicles.team', 'Equipo')}
              defaultValue={selectedVehicleTeam}
              options={[
                {
                  label: t('filters.all'),
                  value: '',
                },
                ...teams.map((team) => ({
                  label: team.name,
                  value: team.id,
                })),
              ]}
            />
          )}
          {showStateFilter && (
            <DropDownFilterStyled
              inputSize={'XS'}
              onChangeSelected={(option) => onSelectStateVehicle(option)}
              typeDropDown="Default"
              placeholder={t('labels.select')}
              options={[
                {
                  label: t('filters.allVehicles'),
                  value: '',
                },
                {
                  label: t('filters.active'),
                  value: 'true',
                },
                {
                  label: t('filters.inactive'),
                  value: 'false',
                },
              ]}
            />
          )}
          <SearchBar
            variant="form"
            onChangeInput={(e) => vehiclesHandleChange(e.target.value)}
          ></SearchBar>
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {vehiclesTable.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableRightTh
                      cell={header.id as variantRightTh}
                      key={header.id}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableRightTh>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {vehiclesTable.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  onClick={() => {
                    if (
                      data?.parentExpeditionOrderStatus !==
                        OrderState.Annulled &&
                      data?.parentExpeditionOrderStatus !== OrderState.End &&
                      data?.state !== OrderState.Annulled
                    ) {
                      handleOnChange(row);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </VehiclesTableContainer>
    </ContainerTable>
  );
};
